import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  Plus,
  FileText,
  Trash2,
  Loader2,
  LogOut,
  Users,
  Clock,
  CheckSquare,
  Activity,
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  PartyPopper,
  Wand2,
  Mail,
  Megaphone,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { formatRelativeTime } from '../utils/time';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon per activity event type for the feed.
const ACTIVITY_ICONS = {
  campaign_created: Megaphone,
  campaign_submitted: CheckSquare,
  campaign_status: CheckSquare,
  creative_complete: Wand2,
  creative_failed: Wand2,
  project_created: FileText,
  email_campaign_created: Mail
};

function StatPill({ label, value, accent = 'text-white' }) {
  return (
    <div>
      <p className="text-xs font-mono text-white/40 uppercase tracking-wider mb-0.5">{label}</p>
      <p className={`text-lg font-bold ${accent}`}>{value}</p>
    </div>
  );
}

// Renders a WoW delta arrow. `goodWhenUp` flips the colour logic (CPL up is bad).
function DeltaArrow({ delta, goodWhenUp = true }) {
  if (delta === null || delta === undefined) {
    return <span className="text-white/30 text-xs flex items-center gap-0.5"><Minus className="w-3 h-3" /> —</span>;
  }

  const isUp = delta > 0;
  const isFlat = delta === 0;
  const isGood = isFlat ? true : (isUp === goodWhenUp);
  const color = isFlat ? 'text-white/40' : isGood ? 'text-green-400' : 'text-red-400';
  const Icon = isFlat ? Minus : isUp ? ArrowUpRight : ArrowDownRight;

  return (
    <span className={`text-xs flex items-center gap-0.5 ${color}`}>
      <Icon className="w-3 h-3" />
      {Math.abs(delta)}%
    </span>
  );
}

function Dashboard() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);

  useEffect(() => {
    loadProjects();
    loadSummary();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects/`);
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error('Failed to load projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to load dashboard summary:', error);
      // Non-fatal: the page still renders projects + CTAs.
      setSummary({ pending_approvals: { count: 0, items: [] }, recent_activity: [], client_scoreboard: [] });
    } finally {
      setLoadingSummary(false);
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success('Project deleted');
      loadProjects();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete project');
    }
  };

  const pending = summary?.pending_approvals || { count: 0, items: [] };
  const activity = summary?.recent_activity || [];
  const scoreboard = summary?.client_scoreboard || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#0a0a0a]">
      {/* Header */}
      <header className="glass-morphism sticky top-0 z-50 px-4 md:px-8 py-4 border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <div className="font-mono text-[#00d4aa] text-xs uppercase tracking-wider mb-1">
              EPSILLON MEDIA
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-white">
              Dashboard
            </h1>
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            <div className="hidden md:block text-sm text-white/60">
              Welcome, <span className="text-white font-semibold">{user?.name}</span>
            </div>

            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
                className="border-white/10"
              >
                <Users className="w-4 h-4 mr-2" />
                Admin
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-white/60 hover:text-white"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-8">
        {/* Top row: Waiting on you + Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Waiting on you */}
          <Card className="card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-[#00d4aa]" />
                  <h2 className="text-lg font-bold text-white">Waiting on you</h2>
                </div>
                {!loadingSummary && pending.count > 0 && (
                  <span className="px-2.5 py-0.5 rounded-full text-sm font-bold bg-[#00d4aa]/20 text-[#00d4aa] border border-[#00d4aa]/30">
                    {pending.count}
                  </span>
                )}
              </div>

              {loadingSummary ? (
                <div className="space-y-3">
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} className="h-14 w-full bg-white/5" />
                  ))}
                </div>
              ) : pending.count === 0 ? (
                <div className="text-center py-8">
                  <PartyPopper className="w-12 h-12 mx-auto mb-3 text-[#00d4aa]/60" />
                  <p className="text-white font-medium mb-1">You're all caught up</p>
                  <p className="text-white/50 text-sm">No campaigns waiting for approval.</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    {pending.items.map((item) => (
                      <button
                        key={item.campaign_id}
                        onClick={() => navigate(item.route)}
                        className="w-full text-left p-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#00d4aa]/30 transition-all group"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-white font-medium truncate group-hover:text-[#00d4aa] transition-colors">
                              {item.name}
                            </p>
                            <p className="text-xs text-white/50 truncate">
                              {item.client || 'No client'} · {item.submitted_by}
                            </p>
                          </div>
                          <span className="text-xs text-white/40 whitespace-nowrap">
                            {formatRelativeTime(item.submitted_at)}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/campaigns/queue')}
                    className="w-full mt-3 text-[#00d4aa] hover:text-[#00e6bb] hover:bg-[#00d4aa]/10"
                  >
                    View approval queue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-5 h-5 text-[#00d4aa]" />
                <h2 className="text-lg font-bold text-white">Recent Activity</h2>
              </div>

              {loadingSummary ? (
                <div className="space-y-3">
                  {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-10 w-full bg-white/5" />
                  ))}
                </div>
              ) : activity.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className="w-12 h-12 mx-auto mb-3 text-white/20" />
                  <p className="text-white/60 text-sm">No activity yet. Get started below.</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {activity.map((event, idx) => {
                    const Icon = ACTIVITY_ICONS[event.type] || Activity;
                    const isFailure = event.type === 'creative_failed';
                    return (
                      <button
                        key={`${event.entity_id}-${idx}`}
                        onClick={() => event.route && navigate(event.route)}
                        className="w-full text-left flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors group"
                      >
                        <div className={`p-1.5 rounded-md ${isFailure ? 'bg-red-500/10' : 'bg-white/5'}`}>
                          <Icon className={`w-4 h-4 ${isFailure ? 'text-red-400' : 'text-[#00d4aa]'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white/90 truncate group-hover:text-white">
                            {event.label}
                          </p>
                          {event.client && (
                            <p className="text-xs text-white/40 truncate">{event.client}</p>
                          )}
                        </div>
                        <span className="text-xs text-white/40 whitespace-nowrap">
                          {formatRelativeTime(event.timestamp)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Client Scoreboard (admin only) */}
        {isAdmin && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-[#00d4aa]" />
              <h2 className="text-lg font-bold text-white">Client Scoreboard</h2>
              <span className="text-xs text-white/40 font-mono">LAST 7 DAYS</span>
            </div>

            {loadingSummary ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-32 w-full bg-white/5 rounded-xl" />
                ))}
              </div>
            ) : scoreboard.length === 0 ? (
              <Card className="card">
                <CardContent className="py-10 text-center">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 text-white/20" />
                  <p className="text-white/60 text-sm">No active clients yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {scoreboard.map((client) => (
                  <Card
                    key={client.client_id}
                    className="card cursor-pointer hover:border-[#00d4aa]/30 transition-all"
                    onClick={() => navigate(`/client-dashboard?client=${client.slug}`)}
                  >
                    <CardContent className="pt-5">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-white truncate">{client.name}</h3>
                        <ArrowRight className="w-4 h-4 text-white/30" />
                      </div>

                      {!client.has_data ? (
                        <p className="text-white/40 text-sm py-4">No metrics yet</p>
                      ) : (
                        <div className="grid grid-cols-2 gap-y-4 gap-x-3">
                          <div>
                            <p className="text-xs font-mono text-white/40 uppercase tracking-wider mb-0.5">Spend</p>
                            <div className="flex items-center gap-2">
                              <p className="text-lg font-bold text-white">${client.spend.toLocaleString()}</p>
                              <DeltaArrow delta={client.spend_delta_pct} goodWhenUp={false} />
                            </div>
                          </div>
                          <div>
                            <p className="text-xs font-mono text-white/40 uppercase tracking-wider mb-0.5">Leads</p>
                            <div className="flex items-center gap-2">
                              <p className="text-lg font-bold text-white">{client.leads}</p>
                              <DeltaArrow delta={client.leads_delta_pct} goodWhenUp={true} />
                            </div>
                          </div>
                          <StatPill label="CPL" value={`$${client.cpl.toFixed(2)}`} />
                          <StatPill label="ROAS" value={`${client.roas.toFixed(2)}x`} accent="text-[#00d4aa]" />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Demoted: New Project CTA + recent projects */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-white">Your Projects</h2>
            <Button
              onClick={() => navigate('/create')}
              className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </div>

          {loadingProjects ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#00d4aa]" />
            </div>
          ) : projects.length === 0 ? (
            <Card className="card">
              <CardContent className="pt-6 text-center py-12">
                <FileText className="w-16 h-16 mx-auto mb-4 text-white/20" />
                <p className="text-white/60 mb-4">No projects yet</p>
                <Button
                  onClick={() => navigate('/create')}
                  className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Project
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map((project) => (
                <Card
                  key={project.id}
                  className="card cursor-pointer group"
                  onClick={() => navigate(`/create/${project.id}`)}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-semibold text-white text-lg group-hover:text-[#00d4aa] transition-colors">
                        {project.title}
                      </h3>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteProject(project.id);
                        }}
                        className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-white/60">
                      <Clock className="w-3 h-3" />
                      {new Date(project.created_at).toLocaleDateString()}
                    </div>

                    <div className="mt-3 px-2 py-1 bg-[#00d4aa]/20 text-[#00d4aa] rounded text-xs inline-block">
                      {project.status}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default Dashboard;
